import BigNumber from 'bignumber.js';
import type { Route } from 'vue-router';
import simpleTween from '@/shared/simpleTween';

/**
 * 去除字符串中全部空格
 */
export function trimAll(str: string): string {
  return str.replace(/\s+/g, '');
}

/**
 * 字符串转数值
 */
export function string2number(str: string | number): number {
  if (typeof str === 'number') return str;
  const value = Number.parseFloat(str);

  if (Number.isNaN(value)) {
    return 0;
  }

  return value;
}

/**
 * 判断是否为Promise对象
 */
export function isPromise<T>(value: unknown): value is Promise<T> {
  return (
    value != null &&
    (typeof value === 'object' || typeof value === 'function') &&
    typeof (value as Promise<T>).then === 'function' &&
    typeof (value as Promise<T>).catch === 'function'
  );
}

/**
 * 获取当前域名
 */
export function getCurrentDomain() {
  const { origin } = window.location;

  return `${origin}${process.env.VUE_APP_PREFIX}`;
}

/**
 * 获取指定元素的scrollTop
 */
export function getScrollTop(el: Element | Window) {
  const top = 'scrollTop' in el ? el.scrollTop : el.scrollY;

  return Math.max(0, top);
}

/**
 * 获取指定元素到页面顶部的距离
 */
export function getOffsetTop(el: HTMLElement | null) {
  if (!el) {
    return 0;
  }

  const win = el.ownerDocument.defaultView;

  return Math.round(el.getBoundingClientRect().top + (win?.pageYOffset ?? 0));
}

/**
 * 获取当前滚动的位置
 */
export function getCurrentScrollTop() {
  return Math.round(document.documentElement.scrollTop);
}

/**
 * 滚动到指定位置
 */
export function scrollTo(end: number, onComplete?: () => void, duration = 400) {
  const scrollTop = getCurrentScrollTop();

  simpleTween({
    start: scrollTop,
    end,
    duration,
    onUpdate(v) {
      window.scrollTo(0, v);
    },
    onComplete,
  });
}

/**
 * vw/vh单位转换像素
 */
export function unit2pixel(value: number | string) {
  if (typeof value === 'number') {
    return value;
  }

  if (value.indexOf('vw') !== -1) {
    return new BigNumber(+value.replace(/vw/g, ''))
      .times(window.innerWidth)
      .div(100)
      .integerValue(BigNumber.ROUND_FLOOR)
      .toNumber();
  }

  if (value.indexOf('vh') !== -1) {
    return new BigNumber(+value.replace(/vh/g, ''))
      .times(window.innerHeight)
      .div(100)
      .integerValue(BigNumber.ROUND_FLOOR)
      .toNumber();
  }

  return parseFloat(value);
}

/**
 * 设置iframe跳转方式为顶部窗口
 */
export function setJumpToTopWindow(iframe: HTMLIFrameElement) {
  if (iframe == null || iframe.contentWindow == null) {
    return;
  }

  const doc = iframe.contentWindow.document;
  const a = doc.querySelectorAll('a');

  if (a) {
    for (let i = 0, item; (item = a[i++]); ) {
      if (item.href !== null && item.href !== '') {
        item.target = '_top';
      }
    }
  }
}

/**
 * 文件数据类型
 */
export const enum FileDataTypes {
  ARRAY_BUFFER = 1,
  BINARY_STRING = 2,
  DATA_URL = 3,
  TEXT = 4,
}

/**
 * 文件转换成指定数据类型
 */
export function fileToDataType(
  file: File,
  dataType: FileDataTypes = FileDataTypes.DATA_URL,
): Promise<string | ArrayBuffer> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      const data = e.target?.result;

      if (data) {
        resolve(data);
      } else {
        reject(new Error('文件解析失败，请重新选择文件'));
      }
    };

    reader.onerror = () => {
      reject(new Error('文件解析失败，请重新选择文件'));
    };

    switch (dataType) {
      case FileDataTypes.ARRAY_BUFFER:
        reader.readAsArrayBuffer(file);
        break;
      case FileDataTypes.BINARY_STRING:
        reader.readAsBinaryString(file);
        break;
      case FileDataTypes.DATA_URL:
        reader.readAsDataURL(file);
        break;
      case FileDataTypes.TEXT:
        reader.readAsText(file);
        break;
    }
  });
}

/**
 * 生成一个随机数字
 */
export function random(min: number, max: number) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

/**
 * 判断是否存在navbar, 自定义navbar不在范围内
 */
export function existsNavBar(route: Route) {
  // 页面地址中设置了hasNavBar为0
  if (route.query.hasNavBar === '0') {
    return false;
  }

  // 判断当前路由是否嵌套在包含了nav-bar的布局中
  const nestedNavBarLayout = route.matched?.find((item) => item.meta.hasNavBarLayout);

  if (!nestedNavBarLayout) {
    return false;
  }

  return true;
}

/**
 * 判断元素是否隐藏
 */
export function isElementHidden(el: HTMLElement) {
  const style = window.getComputedStyle(el);
  const hidden = style.display === 'none';
  // offsetParent returns null in the following situations:
  // 1. The element or its parent element has the display property set to none.
  // 2. The element has the position property set to fixed
  const parentHidden = el.offsetParent === null && style.position !== 'fixed';

  return hidden || parentHidden;
}

/**
 * 获取滚动元素父级
 */
const overflowScrollRegex = /scroll|auto|overlay/i;
export function getScrollParent(el?: HTMLElement | null, root: HTMLElement | Window = window) {
  let node = el;

  while (
    node &&
    node.tagName !== 'HTML' &&
    node.tagName !== 'BODY' &&
    node.nodeType === 1 &&
    node !== root
  ) {
    const { overflowY } = window.getComputedStyle(node);
    if (overflowScrollRegex.test(overflowY)) {
      return node;
    }

    node = node.parentNode as HTMLElement;
  }

  return root;
}

/**
 * 添加px单位
 */
export function addUnit(value?: string | number) {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return `${value}px`;
  }

  return value;
}

/**
 * 获取页面标题
 */
export function getTitle(route: Route): string {
  if (route.path === '/protocol/agreement') {
    switch (route.query.type) {
      case '1':
        return 'Acuerdo de Usuario';
      case '2':
        return 'Aviso de Privacidad';
      case '4':
        return 'Política de Obtención y Tratamiento ';
      default:
        break;
    }
  }
  if (route.meta?.title) {
    return route.meta.title;
  }

  // TODO:
  return 'Orale';
}

/**
 * 判断是否为有效数组
 */
export function isValidArray(arr: any): arr is any[] {
  return Array.isArray(arr) && arr.length > 0;
}

/**
 * 判断是否为有效时间
 */
export function isValidDate(date: Date) {
  return date instanceof Date && !isNaN(date.getTime());
}

/**
 * 图片加载器
 * @param url
 */
export function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();

    image.onload = () => {
      resolve(image);
    };

    image.onerror = () => {
      reject(new Error('图片加载失败'));
    };

    image.src = url;
  });
}
